import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore'
import EmotionSelectModal from './EmotionSelectModal'
import AnalyzingModal from '../Canvas/AnalyzingModal'
import styles from './DiaryWrite.module.css'
import { AlertModal } from '../AlertModal'

const DiaryWrite = ({ onNavigateToDiary, onNavigateToDiaryResult }) => {
  const location = useLocation()
  const emotionFromState = location.state?.selectedEmotion
  const dateFromState = location.state?.selectedDate 
  
  const [showEmotionModal, setShowEmotionModal] = useState(!emotionFromState)
  const [selectedEmotion, setSelectedEmotion] = useState(emotionFromState || null)
  const [diaryTitle, setDiaryTitle] = useState('')
  const [diaryContent, setDiaryContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  
  // 선택된 날짜 또는 오늘 날짜 사용 (Date 객체로 변환)
  const selectedDate = dateFromState 
    ? (dateFromState instanceof Date ? dateFromState : new Date(dateFromState))
    : new Date()
  
  const currentDate = `${selectedDate.getMonth() + 1}/${selectedDate.getDate()} (${['일', '월', '화', '수', '목', '금', '토'][selectedDate.getDay()]})`

  const emotionColors = {
    happy: '#FF6B9D',
    excited: '#8B6914',
    love: '#B8405E',
    calm: '#5F7161',
    anxious: '#8B8B4F',
    proud: '#5B8BA8',
    tired: '#4A5568',
    sad: '#4682B4',
    lonely: '#9CA3AF',
    angry: '#654321',
    confused: '#607D8B',
    depressed: '#8B5A8B'
  }

  const handleEmotionSelect = (emotion) => {
    setSelectedEmotion(emotion)
    setShowEmotionModal(false)
  }

  const emotionKeywordMap = {
    happy: 'HAPPINESS',
    excited: 'EXPECTATION',
    love: 'LOVE',
    calm: 'PEACE',
    anxious: 'CONCERN',
    proud: 'PRIDE',
    tired: 'FATIGUE',
    sad: 'SADNESS',
    lonely: 'LONELINESS',
    angry: 'ANGER',
    confused: 'CONFUSION',
    depressed: 'DEPRESSION',
  }

  const handleSubmit = async () => {
    if (!selectedEmotion) {
      setAlertMessage('감정을 먼저 선택해주세요.')
      setShowAlert(true)
      return
    }
    if (!diaryTitle.trim()) {
      setAlertMessage('제목을 입력해주세요.')
      setShowAlert(true)
      return
    }
    if (!diaryContent.trim()) {
      setAlertMessage('일기 내용을 입력해주세요.')
      setShowAlert(true)
      return
    }

    const keyword = emotionKeywordMap[selectedEmotion.id]
    if (!keyword) {
      setAlertMessage('선택한 감정에 해당하는 키워드를 찾을 수 없습니다.')
      setShowAlert(true)
      return
    }
    
    try {
      setIsSubmitting(true)
      setIsAnalyzing(true)

      const token = getAccessToken()
      if (!token) {
        setAlertMessage('로그인 정보가 없습니다. 다시 로그인해주세요.')
        setShowAlert(true)
        setIsSubmitting(false)
        setIsAnalyzing(false)
        return
      }

      // writtenAt은 "달력에서 선택한 날짜" 기준으로 전송
      // 타임존 변환으로 하루가 밀리지 않도록, 항상 해당 날짜의 오후 6시(18:00)로 고정
      const baseDate =
        selectedDate instanceof Date ? new Date(selectedDate) : new Date()
      baseDate.setHours(18, 0, 0, 0)

      const writtenAt = baseDate.toISOString()

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE}/api/diaries`,
        {
          writtenAt,
          keyword,
      title: diaryTitle,
      content: diaryContent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const apiDiary = response.data?.data

      const diaryData = {
        id: apiDiary?.diaryId,
        date: apiDiary?.writtenAt || writtenAt,
        emotion: selectedEmotion,
        title: apiDiary?.title || diaryTitle,
        content: apiDiary?.content || diaryContent,
        imageUrl: apiDiary?.imageUrl || null,
        keyword: apiDiary?.keyword || keyword,
      }

    onNavigateToDiaryResult(diaryData)
    } catch (error) {
      console.error('일기 작성 API 요청 실패:', error)
      if (error.response?.status === 500) {
        setAlertMessage('제출 중 오류가 발생하였습니다. 잠시 후 다시 제출해주세요')
      } else if (error.response?.data?.message) {
        setAlertMessage(error.response.data.message)
      } else {
        setAlertMessage('일기 작성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
      }
      setShowAlert(true)
    } finally {
      setIsSubmitting(false)
      setIsAnalyzing(false)
    }
  }

  const backgroundColor = selectedEmotion 
    ? emotionColors[selectedEmotion.id] 
    : '#7d57d1'

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '125, 87, 209'
  }

  const emotionColorRgb = hexToRgb(backgroundColor)

  return (
    <>
      <EmotionSelectModal
        isOpen={showEmotionModal}
        onClose={onNavigateToDiary}
        onSelectEmotion={handleEmotionSelect}
        date={currentDate}
      />

      <AnalyzingModal
        isOpen={isAnalyzing}
        onComplete={() => {}}
        title="일기 분석중"
        description="AI가 당신의 하루를 분석하고 있어요."
        duration={10000}
      />

      {selectedEmotion && (
        <div 
          className={styles.wrapper}
          style={{ 
            '--emotion-color': backgroundColor,
            '--emotion-color-rgb': emotionColorRgb
          }}
        >
          <div className={styles.container}>
            <div className={styles.header}>
              <button className={styles.backButton} onClick={onNavigateToDiary}>
                <i className="fas fa-chevron-left"></i>
              </button>
            </div>

            <div className={styles.content}>
              <div className={styles.dateSection}>
                <div className={styles.dateMain}>
                  {selectedDate.toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                <div className={styles.emotionTag}>
                  <span>#{selectedEmotion.label}</span>
                </div>
              </div>

              <div className={styles.titleSection}>
                <input
                  type="text"
                  className={styles.titleInput}
                  placeholder="제목을 입력해주세요"
                  value={diaryTitle}
                  onChange={(e) => setDiaryTitle(e.target.value)}
                  maxLength={50}
                />
                <div className={styles.titleCharCount}>{diaryTitle.length} / 50</div>
              </div>

              <div className={styles.diarySection}>
                <textarea
                  className={styles.diaryInput}
                  placeholder="오늘 하루는 어떠셨나요?&#10;자유롭게 작성해보세요."
                  value={diaryContent}
                  onChange={(e) => setDiaryContent(e.target.value)}
                  maxLength={1000}
                />
                <div className={styles.charCount}>{diaryContent.length} / 1000</div>
              </div>

              <button 
                className={styles.submitButton} 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                <i className="fas fa-check"></i>
                <span>{isSubmitting ? '일기를 저장하는 중...' : '일기 완성하기'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </>
  )
}

export default DiaryWrite
